const DASHED_UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const HEX_32_REGEX = /^[0-9a-f]{32}$/i;
/** Local 0… (10 digits) or country-code + national number (10–11 digits), optional leading +. */
const PHONE_LIKE_SCAN_REGEX = /^\+?\d{10,11}$/;

const AZERTY_DIGIT_KEYS: Record<string, string> = {
  '&': '1',
  é: '2',
  '"': '3',
  '“': '3',
  "'": '4',
  '(': '5',
  '-': '6',
  è: '7',
  _: '8',
  ç: '9',
  à: '0',
  ')': '0',
};

const AZERTY_LETTER_KEYS: Record<string, string> = {
  q: 'a',
  Q: 'A',
};

function replaceAzertySpecialChars(input: string): string {
  return input.replace(/./g, match => AZERTY_DIGIT_KEYS[match] ?? match);
}

function replaceAzertyLetters(input: string): string {
  return input.replace(/./g, match => AZERTY_LETTER_KEYS[match] ?? match);
}

function cleanScannedCode(raw: string): string {
  return replaceAzertyLetters(replaceAzertySpecialChars(raw.trim()));
}

/** Insert RFC-4122 dashes into a 32-char hex payload. */
export function insertUuidDashes(hex32: string): string {
  return `${hex32.slice(0, 8)}-${hex32.slice(8, 12)}-${hex32.slice(12, 16)}-${hex32.slice(16, 20)}-${hex32.slice(20)}`;
}

/**
 * True when the scanned buffer is a phone number (10–11 digits), not a 4-digit
 * OTP or a Como identification UUID. Covers local 0… numbers and international
 * payloads such as Belgian 32475871902 (32 + 9 digits, leading 0 dropped).
 */
export function isPhoneLikeIdentificationCode(raw: string): boolean {
  const compact = cleanScannedCode(raw).replace(/[\s.]/g, '');
  return PHONE_LIKE_SCAN_REGEX.test(compact);
}

/**
 * Normalize a kiosk scanner payload:
 * - already dashed UUID → unchanged
 * - 32 hex (Wallet Pass without dashes, or AZERTY dashes flattened to 32 chars) → insert dashes
 * - anything else (4-digit OTP, other short codes) → AZERTY cleanup only
 */
export function ensureKioskScanCodeEncoding(raw: string): string {
  const trimmed = raw.trim();
  if (DASHED_UUID_REGEX.test(trimmed)) {
    return trimmed.toLowerCase();
  }

  const cleaned = cleanScannedCode(trimmed);
  if (HEX_32_REGEX.test(cleaned)) {
    return insertUuidDashes(cleaned.toLowerCase());
  }

  return cleaned;
}

/**
 * Normalize a scanned phone so attach-user receives the same shape as keypad
 * identification: local 0… stays local, country-coded digits get a leading +.
 */
export function normalizePhoneLikeScan(raw: string): string {
  const compact = cleanScannedCode(raw).replace(/[\s.]/g, '');
  if (compact.startsWith('+') || compact.startsWith('0')) {
    return compact;
  }
  return `+${compact}`;
}
